import { Directive, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, Renderer2, SimpleChanges } from '@angular/core';
import { fromEvent } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { GooglePlacesService } from '../services/google-places.service';
import { Loader } from '@googlemaps/js-api-loader';
import { environment } from '../environments/environment';

declare const google: any;

const Bordeaux_Bastide_Start_Latitude = 35.873;
const Bordeaux_Bastide_Start_Longitude = -12.9262;

const Bordeaux_Bastide_End_Latitude = 71.7126;
const Bordeaux_Bastide_End_Longitude = 30.6674;

type FormattedAddress = {
  locality?: string;
  admin_area_l1?: string;
  admin_area_l2?: string;
  admin_area_l3?: string;
  admin_area_l4?: string;
  admin_area_l5?: string;
  admin_area_l6?: string;
  admin_area_l7?: string;
  street_number?: string;
  route?: string;
  country?: string;
  countryCode?: string;
  postal_code?: string;
  postal_town?: string;
  lat?: number;
  lon?: number;
  place_id?: string;
};

@Directive({
  selector: '[jhiGooglePlaces]',
  standalone: true,
})
export class GooglePlacesDirective implements OnChanges, OnInit {
  @Input() gplaceName!: string;
  @Input() colorClass!: string;
  @Output() selectEvent: EventEmitter<any> = new EventEmitter();

  element!: HTMLInputElement;
  marker?: HTMLElement;
  isLoading = false;
  keyCodeListener!: (event: any) => void;

  googleServiceLoader = new Loader({
    apiKey: environment.googleMapApiKey,
    version: 'weekly',
    libraries: ['places'],
  });
  googleServicePlaces!: google.maps.PlacesLibrary;
  googleServiceMap!: google.maps.MapsLibrary;
  googleServiceGeocoding!: google.maps.GeocodingLibrary;
  private sessionToken?: google.maps.places.AutocompleteSessionToken = undefined; // Stockage du jeton de session

  constructor(private renderer: Renderer2, private googlePlaceService: GooglePlacesService, elRef: ElementRef) {
    this.element = elRef.nativeElement;
  }

  ngOnInit(): void {
    Promise.all([this.googleServiceLoader.importLibrary('places'), this.googleServiceLoader.importLibrary('maps'), this.googleServiceLoader.importLibrary('geocoding')])
      .then(value => {
        this.googleServicePlaces = value[0];
        this.googleServiceMap = value[1];
        this.googleServiceGeocoding = value[2];

        this.initLoader();
        this.initGplaceValidMarker();
        if (this.colorClass) {
          this.changeMarkerColorClass(this.colorClass);
        }
        this.initAutocomplete();
      })
      .catch(reason => console.error(reason));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['colorClass']) {
      this.changeMarkerColorClass(changes['colorClass'].currentValue);
    }
  }

  private changeMarkerColorClass(colorClass: string): void {
    this.marker?.classList.remove('gplace-invalid-address');
    this.marker?.classList.remove('gplace-valid-address');
    this.marker?.classList.add(colorClass);
  }

  private initAutocomplete(): void {
    const autocompleteFormField = this.element;
    // Initializes Google Places Autocomplete
    this.initGooglePlacesAutocomplete(autocompleteFormField);
  }

  private predictionListMarkup(): HTMLDivElement {
    const predictionsWrapperDiv = document.createElement(`div`);
    predictionsWrapperDiv.classList.add(this.gplaceName, `pac-container`, `pac-logo`);
    predictionsWrapperDiv.style.setProperty('display', `none`);
    return predictionsWrapperDiv;
  }

  private initGooglePlacesAutocomplete(autocompleteFormField: HTMLInputElement): void {
    // Créer un nouveau jeton de session au début de l'interaction utilisateur
    this.sessionToken = new this.googleServicePlaces.AutocompleteSessionToken();

    const predictionList = this.predictionListMarkup();
    this.renderer.appendChild(document.body, predictionList);

    fromEvent(autocompleteFormField, 'focus').subscribe(() => {
      // Créer un nouveau jeton de session au focus de l'input
      this.sessionToken = new this.googleServicePlaces.AutocompleteSessionToken();
    });

    fromEvent(autocompleteFormField, 'input')
      .pipe(
        tap(() => this.toggleLoading(autocompleteFormField.value.length > 0)),
        tap(() => this.marker?.classList.remove('gplace-valid-address')),
        debounceTime(500)
      )
      .subscribe(() => {
        if (autocompleteFormField.value) {
          // Ne pas lancer la requête si moins de 3 caractères
          if (autocompleteFormField.value.length < 3) {
            this.toggleLoading(false);
            return;
          }

          predictionList.style.display = `block`;
          const LatLngBordeauxStartBastide = new google.maps.LatLng(Bordeaux_Bastide_Start_Latitude, Bordeaux_Bastide_Start_Longitude);
          const LatLngBordeauxEndBastide = new google.maps.LatLng(Bordeaux_Bastide_End_Latitude, Bordeaux_Bastide_End_Longitude);
          const defaultBounds = new google.maps.LatLngBounds(LatLngBordeauxStartBastide, LatLngBordeauxEndBastide);

          const autocompleteRequest: google.maps.places.AutocompleteRequest = {
            input: autocompleteFormField.value,
            locationRestriction: defaultBounds,
            region: 'eu',
            language: 'fr',
            sessionToken: this.sessionToken, // Utilisation du jeton de session
          };
          this.googleServicePlaces.AutocompleteSuggestion.fetchAutocompleteSuggestions(autocompleteRequest)
            .then(autoCompleteSuggestionsResponse => {
              this.displayPredictionSuggestions(autoCompleteSuggestionsResponse.suggestions, predictionList, autocompleteFormField);
            })
            .catch(err => console.error(err));
        } else {
          predictionList.style.display = `none`;
        }
      });
  }

  private predictionBuilder(placePrediction: google.maps.places.PlacePrediction, predictionList: HTMLDivElement): void {
    // strict prediction filtering based on the street address verification.
    const predictionListItem = document.createElement(`div`);
    predictionListItem.classList.add(`pac-item`);

    const spanIcon = document.createElement('span');
    spanIcon.classList.add('pac-icon', 'pac-icon-marker');

    const spanItemQuery = document.createElement('span');
    spanItemQuery.classList.add('pac-item-query');

    const spanStart = "<span class='pac-matched'>";
    const spanEnd = '</span>';
    const result = placePrediction.text.text;

    const workingElement = result.split('');
    placePrediction.text.matches.forEach(position => {
      workingElement[position.startOffset] = spanStart + workingElement[position.startOffset];
      workingElement[position.endOffset] = workingElement[position.endOffset] + spanEnd;
    });
    spanItemQuery.innerHTML = workingElement.join('');
    spanItemQuery.inert;

    predictionListItem.appendChild(spanIcon);
    predictionListItem.appendChild(spanItemQuery);

    predictionListItem.addEventListener(`click`, (): void => {
      this.autocompleteServiceListener(placePrediction, predictionList);
    });
    predictionList.appendChild(predictionListItem);
  }

  private autocompleteServiceListener(prediction: google.maps.places.PlacePrediction, predictionList: HTMLDivElement): void {
    const wantedPlaceFields: google.maps.places.FetchFieldsRequest = {
      fields: ['id', 'addressComponents', 'types', 'displayName', 'location', 'formattedAddress'],
    };
    prediction
      .toPlace()
      .fetchFields(wantedPlaceFields)
      .then(value => {
        const formattedAddress: FormattedAddress = this.getFormattedAddress(value.place);

        // todo rework that
        if (!formattedAddress.postal_code) {
          this.getZipCodeFromGeoCoder(formattedAddress).then(() => {
            this.prepareDataForEmit(formattedAddress);
            predictionList.style.display = `none`;
          });
        } else {
          this.prepareDataForEmit(formattedAddress);
          predictionList.style.display = `none`;
        }
      })
      .catch(err => {
        console.error('Error getting place detail:', err);
        predictionList.style.display = `none`;
      });
  }

  private getFormattedAddress(place: google.maps.places.Place): FormattedAddress {
    const locationObj = Object.create({});
    place.addressComponents?.forEach(component => {
      if (component.types.includes('locality')) {
        locationObj.locality = component.longText;
      } else if (component.types.includes('administrative_area_level_1')) {
        locationObj.admin_area_l1 = component.shortText;
      } else if (component.types.includes('administrative_area_level_2')) {
        locationObj.admin_area_l2 = component.shortText;
      } else if (component.types.includes('administrative_area_level_3')) {
        locationObj.admin_area_l3 = component.shortText;
      } else if (component.types.includes('administrative_area_level_4')) {
        locationObj.admin_area_l4 = component.shortText;
      } else if (component.types.includes('administrative_area_level_5')) {
        locationObj.admin_area_l5 = component.shortText;
      } else if (component.types.includes('administrative_area_level_6')) {
        locationObj.admin_area_l6 = component.shortText;
      } else if (component.types.includes('administrative_area_level_7')) {
        locationObj.admin_area_l7 = component.shortText;
      } else if (component.types.includes('street_number')) {
        locationObj.street_number = component.shortText;
      } else if (component.types.includes('route')) {
        locationObj.route = component.longText;
      } else if (component.types.includes('country')) {
        locationObj.country = component.longText;
        locationObj.countryCode = component.shortText;
      } else if (component.types.includes('postal_code')) {
        locationObj.postal_code = component.shortText;
      } else if (component.types.includes('postal_town')) {
        locationObj.postal_town = component.shortText;
      }
    });

    // Si c'est un établissement
    const isEstablishment = place.types?.includes('establishment') || false;

    if (isEstablishment) {
      locationObj.fullname = place.displayName;
      // If no road found, we use place name
      if (!locationObj.route) {
        locationObj.route = place.displayName;
      }
    }

    locationObj.lat = place.location?.lat();
    locationObj.lon = place.location?.lng();
    locationObj.place_id = place.id;

    return locationObj;
  }

  private getZipCodeFromGeoCoder(formattedAddress: FormattedAddress): Promise<boolean> {
    const hasGeoLocation = !!formattedAddress.lat && !!formattedAddress.lon;
    if (!hasGeoLocation) {
      return Promise.reject('No geocode available');
    }

    const geocoderService = new this.googleServiceGeocoding.Geocoder();
    const geocoderRequest: google.maps.GeocoderRequest = {
      location: {
        lng: formattedAddress.lon || 0, //Cannot happen protected by if on hasGeoLocation,
        lat: formattedAddress.lat || 0,
      },
    };

    return new Promise<boolean>(resolve => {
      geocoderService.geocode(geocoderRequest).then(response => {
        // élément variable de google, j'ai pas trouvé de type
        // recherche dans les types directs
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        const results = response.results.filter((it: any) => it.types.includes('postal_code'));
        if (results.length > 0) {
          formattedAddress['postal_code'] = results[0].address_components[0].short_name;
          resolve(true);
        } else {
          try {
            // on tente une recherche dans les sous types
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            const parentWithPostalCode = response.results.filter((it: any) => it.address_components.find((subIt: any) => subIt.types.includes('postal_code')));
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            const postalCodes = parentWithPostalCode[0].address_components.find((subIt: any) => subIt.types.includes('postal_code'));

            if (postalCodes?.short_name) {
              formattedAddress['postal_code'] = postalCodes.short_name;
              resolve(true);
            } else {
              console.error('No postal code find !!');
              resolve(false);
            }
          } catch (e) {
            resolve(false);
          }
        }
      });
    });
  }

  private prepareDataForEmit(formattedAddress: FormattedAddress): void {
    this.googlePlaceService.setAddress(formattedAddress).subscribe(address => {
      this.selectEvent.emit(address);
      if (this.colorClass) {
        this.changeMarkerColorClass(this.colorClass);
      } else {
        this.changeMarkerColorClass('gplace-valid-address');
      }
    });
  }

  private displayPredictionSuggestions(suggestions: google.maps.places.AutocompleteSuggestion[], predictionList: HTMLDivElement, autocompleteFormField: HTMLInputElement): void {
    // If the service is down, do not return results.
    if (!suggestions.length) {
      predictionList.style.display = `none`;
      this.toggleLoading(false);
      return;
    }

    predictionList.innerHTML = '';

    // met à jour le status
    const bounding = this.element.getBoundingClientRect();
    predictionList.style.setProperty('width', bounding.width + 'px');
    predictionList.style.setProperty('position', 'absolute');
    predictionList.style.setProperty('left', bounding.left + 'px');

    const val = bounding.top + bounding.height + window.scrollY;
    predictionList.style.setProperty('top', val + 'px');

    autocompleteFormField.removeEventListener(`keydown`, this.keyCodeListener); // remove all listener
    for (const suggestion of suggestions) {
      if (suggestion.placePrediction) {
        this.predictionBuilder(suggestion.placePrediction, predictionList);
      }
    }
    this.autocompleteKeyboardListener(suggestions, predictionList, autocompleteFormField);
    this.toggleLoading(false);
  }

  private autocompleteKeyboardListener(predictions: google.maps.places.AutocompleteSuggestion[], predictionList: HTMLDivElement, autocompleteFormField: HTMLInputElement): void {
    const autocompletePredictionMarkup = document.querySelector('.' + this.gplaceName + `.pac-container`);
    this.keyCodeListener = event => {
      switch (event.keyCode) {
        case 38:
          // UP
          this.upKeyAutocompleteInteraction(autocompletePredictionMarkup!, autocompleteFormField);
          break;

        case 40:
          // DOWN
          this.downKeyAutocompleteInteraction(autocompletePredictionMarkup!, autocompleteFormField);
          break;

        case 9:
          // TAB
          this.keyboardAutocomplete(predictions, predictionList, autocompleteFormField, this.keyCodeListener);
          break;

        case 13:
          // ENTER
          this.keyboardAutocomplete(predictions, predictionList, autocompleteFormField, this.keyCodeListener);
          break;
      }
    };
    autocompleteFormField.addEventListener(`keydown`, this.keyCodeListener);
  }

  private upKeyAutocompleteInteraction(
    autocompletePredictionMarkup: {
      lastChild: any;
    },
    autocompleteFormField: HTMLInputElement
  ): void {
    if (!document.querySelector(`.pac-item-selected`)) {
      // Returns the bottom of the list.
      return this.autocompleteListDecorator(autocompletePredictionMarkup.lastChild, autocompleteFormField);
    } else {
      // Transverse the list in reverse order.
      const previousSibling = document.querySelector(`.pac-item-selected`)?.previousSibling;
      if (previousSibling) {
        this.autocompleteListDecorator(previousSibling, autocompleteFormField);
      } else {
        // Return to the last list element at the top of the list.
        this.autocompleteListDecorator(autocompletePredictionMarkup.lastChild, autocompleteFormField);
      }
    }
  }

  private downKeyAutocompleteInteraction(
    autocompletePredictionMarkup: {
      firstChild: any;
    },
    autocompleteFormField: HTMLInputElement
  ): void {
    if (!document.querySelector(`.pac-item-selected`)) {
      // Start at the top of the list.
      return this.autocompleteListDecorator(autocompletePredictionMarkup.firstChild, autocompleteFormField);
    } else {
      // Transverse the list from top down.
      const nextSibling = document.querySelector(`.pac-item-selected`)?.nextSibling;
      if (nextSibling) {
        this.autocompleteListDecorator(nextSibling, autocompleteFormField);
      } else {
        // Return to the first list element at the end of the list.
        this.autocompleteListDecorator(autocompletePredictionMarkup.firstChild, autocompleteFormField);
      }
    }
  }

  private autocompleteListDecorator(autocompletePredictionMarkup: any, autocompleteFormField: HTMLInputElement): void {
    if (document.querySelector(`.pac-item-selected`)) {
      document.querySelector(`.pac-item-selected`)?.classList.remove(`pac-item-selected`);
    }
    autocompleteFormField.value = autocompletePredictionMarkup.innerText;
    autocompletePredictionMarkup.classList.add(`pac-item-selected`);
  }

  private keyboardAutocomplete(predictions: any, predictionList: HTMLDivElement, autocompleteFormField: HTMLInputElement, keyCodeListener: any): void {
    if (document.querySelector(`.pac-item-selected`)?.innerHTML) {
      for (const prediction of predictions) {
        if ((document.querySelector(`.pac-item-selected`) as HTMLElement)?.innerText === prediction.description) {
          this.autocompleteServiceListener(prediction, predictionList);
        }
      }
      document.querySelector(`.pac-item-selected`)?.classList.remove(`pac-item-selected`);
      autocompleteFormField.removeEventListener(`keydown`, keyCodeListener);
    }
  }

  private initLoader(): void {
    const loader = document.createElement('div');
    loader.classList.add('gplace-icon-container');
    loader.style.setProperty('display', 'none');
    loader.style.setProperty('top', this.element.offsetHeight / 2 - 10 + 'px');

    const loaderItem = document.createElement('div');
    loaderItem.classList.add('gplace-loader');

    loader.appendChild(loaderItem);
    this.element.parentElement?.appendChild(loader);
  }

  private initGplaceValidMarker(): void {
    this.marker = document.createElement('i');
    this.marker.classList.add('gplace-suffix', 'fas', 'fa-map-marked', 'fa-sm');
    this.marker.style.setProperty('top', this.element.offsetHeight / 2 + 'px');
    this.element.parentElement?.appendChild(this.marker);
  }

  private toggleLoading(active: boolean): void {
    const loader: HTMLDivElement | undefined = this.element.parentElement?.getElementsByClassName('gplace-icon-container')[0] as HTMLDivElement;
    loader?.style.setProperty('display', active ? 'block' : 'none');
  }
}
